import type { JSX } from "preact";
import { useCallback, useMemo } from "preact/hooks";
import type { Descendant, Editor } from "slate";
import { createEditor } from "slate";
import { withHistory } from "slate-history";
import type { RenderElementProps, RenderLeafProps } from "slate-react";
import { Editable, Slate, withReact } from "slate-react";
import { classNames } from "@gss/common/classNames";
import { withImages } from "./AddImageButton";
import { withLink } from "./AddLinkButton";
import { withTables } from "./AddTableButton";
import { Element } from "./Element";
import { Leaf } from "./Leaf";
import { Toolbar } from "./Toolbar";
import "./textEditor.css";
import { getEmptyDescendants } from "./util";

interface Parameters {
    value: Descendant[];
    readOnly?: boolean;
    isInvalid?: boolean;
    placeholder?: string;
    onChange?: (values: Descendant[]) => void;
}

export function TextEditor({
    value,
    readOnly,
    isInvalid,
    placeholder,
    onChange,
}: Parameters): JSX.Element {
    const editor = useMemo(() => createCustomEditor(), []);
    const renderElement = useCallback(
        (props: RenderElementProps) => <Element {...props} />,
        [],
    );
    const renderLeaf = useCallback(
        (props: RenderLeafProps) => <Leaf {...props} />,
        [],
    );

    return (
        <div
            className={classNames(
                "text-editor",
                readOnly && "read-only",
                isInvalid && "invalid",
            )}
        >
            <Slate
                editor={editor}
                initialValue={value.length > 0 ? value : getEmptyDescendants()}
                onChange={onChange}
            >
                {!readOnly && <Toolbar />}

                <Editable
                    className="text-editor-editable"
                    readOnly={readOnly}
                    renderElement={renderElement}
                    renderLeaf={renderLeaf}
                    placeholder={placeholder}
                    spellcheck
                />
            </Slate>
        </div>
    );
}

function createCustomEditor(): Editor {
    return withLink(
        withTables(withImages(withHistory(withReact(createEditor())))),
    );
}
